import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AvailableEarningCharge,
  PaymentChargeThinDTO,
  StripeTransferDTO,
  buildCashOutChargePayload,
  fetchAvailableEarningCharges,
  fetchDefaultPayoutAccount,
  fetchEffectivePayoutCharges,
  fetchPayoutActivitiesReceived,
  fetchReceivablePaymentActivities,
  StripeExternalPayoutAccount,
  submitEarningsCashOut,
} from '../../services/paymentService';
import { PaymentRows } from '../../components/payments/PaymentRows';
import { formatDate, formatMoney, methodLabel } from '../../utils/paymentFormatters';
import '../../styles/AccountTools.css';

type ActivityTab = 'earnings' | 'payouts';

const Earnings = () => {
  const { user } = useAuth();
  const actorId = user?.id || '';
  const isHelper = String(user?.currentAppRole || '').toUpperCase() === 'HELPER';

  const [receivable, setReceivable] = useState<PaymentChargeThinDTO[]>([]);
  const [payouts, setPayouts] = useState<StripeTransferDTO[]>([]);
  const [activityTab, setActivityTab] = useState<ActivityTab>('earnings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [availableEarnings, setAvailableEarnings] = useState(0);
  const [availableCharges, setAvailableCharges] = useState<AvailableEarningCharge[]>([]);
  const [payoutChargesTotal, setPayoutChargesTotal] = useState(0);
  const [defaultPayoutAccount, setDefaultPayoutAccount] = useState<StripeExternalPayoutAccount | null>(null);
  const [cashOutOpen, setCashOutOpen] = useState(false);

  const netCashOutAmount = Math.max(0, availableEarnings - payoutChargesTotal);

  const loadEarnings = useCallback(async () => {
    if (!actorId) return;
    setLoading(true);
    try {
      const [receivableRows, payoutRows, earningCharges, payoutAccount] = await Promise.all([
        fetchReceivablePaymentActivities(),
        fetchPayoutActivitiesReceived(actorId).catch(() => []),
        fetchAvailableEarningCharges(actorId).catch(() => []),
        fetchDefaultPayoutAccount(actorId).catch(() => null),
      ]);

      const availableTotal = (earningCharges || []).reduce(
        (sum, charge) => sum + Number(charge.receivableAmount || charge.amount || 0),
        0
      );

      setReceivable(receivableRows);
      setPayouts(payoutRows);
      setAvailableCharges(earningCharges || []);
      setAvailableEarnings(availableTotal);
      setDefaultPayoutAccount(payoutAccount);

      if (availableTotal > 0) {
        try {
          const address = (user as any)?.permanentAddress || (user as any)?.address || {};
          const effectiveCharges = await fetchEffectivePayoutCharges({
            amount: availableTotal,
            country: address.country,
            state: address.state,
            county: address.county,
            city: address.city,
          });
          const payoutPayload = buildCashOutChargePayload(effectiveCharges, availableTotal);
          setPayoutChargesTotal(payoutPayload.reduce((sum, item) => sum + Number(item.amount || 0), 0));
        } catch {
          setPayoutChargesTotal(0);
        }
      } else {
        setPayoutChargesTotal(0);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to load earnings.',
      });
    } finally {
      setLoading(false);
    }
  }, [actorId, user]);

  useEffect(() => {
    loadEarnings();
  }, [loadEarnings]);

  const activityRows = useMemo(() => {
    if (activityTab === 'payouts') {
      return payouts.map((item) => ({
        id: `payout-${item.id}`,
        title: item.stripeTransferId || 'Payout',
        amount: formatMoney(item.netPayout || item.amount, item.currency || 'USD'),
        meta: `${item.status || 'Status unavailable'} - ${formatDate(item.transferredAt || item.createdAt)}`,
      }));
    }

    return receivable.map((item) => ({
      id: `receivable-${item.id}`,
      title: item.description || item.chargeItemCode || 'Earning',
      amount: formatMoney(item.receivableAmount || item.amount, item.currency || 'USD'),
      meta: `${item.paymentStatus || item.status || 'Status unavailable'} - ${formatDate(item.createdAt)}`,
    }));
  }, [activityTab, payouts, receivable]);

  const handleCashOut = async () => {
    if (!actorId || availableEarnings <= 0 || !availableCharges.length) {
      setMessage({ type: 'error', text: 'You have no available earnings to cash out.' });
      return;
    }
    if (netCashOutAmount <= 0) {
      setMessage({
        type: 'error',
        text: 'Your payout deductions are equal to or greater than your available earnings.',
      });
      return;
    }
    if (!defaultPayoutAccount) {
      setMessage({ type: 'error', text: 'Add a default payout method in Wallet before cashing out.' });
      return;
    }

    setSaving(true);
    try {
      const address = (user as any)?.permanentAddress || (user as any)?.address || {};
      const effectiveCharges = await fetchEffectivePayoutCharges({
        amount: availableEarnings,
        country: address.country,
        state: address.state,
        county: address.county,
        city: address.city,
      });
      const payoutPayload = buildCashOutChargePayload(effectiveCharges, availableEarnings);
      const totalCharges = payoutPayload.reduce((sum, item) => sum + Number(item.amount || 0), 0);

      await submitEarningsCashOut(
        {
          chargeIds: availableCharges.map((charge) => charge.id),
          currency: 'USD',
          totalAmount: availableEarnings,
          totalCharges,
          netPayout: Math.max(0, availableEarnings - totalCharges),
          charges: payoutPayload,
        },
        actorId
      );

      setCashOutOpen(false);
      setMessage({ type: 'success', text: `Cash out submitted for ${formatMoney(netCashOutAmount)}.` });
      await loadEarnings();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Unable to process cash out.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isHelper) {
    return <Navigate to="/account/payments" replace />;
  }

  if (!actorId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to view earnings.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <p className="tool-breadcrumb"><Link to="/account/payments">Payments &amp; Credits</Link> / Earnings</p>
          <h1>Earnings</h1>
          <p>Payments received and payouts for your helper account.</p>
        </div>
        <button className="tool-btn-secondary" type="button" onClick={loadEarnings} disabled={loading || saving}>
          Refresh
        </button>
      </div>

      {message && <div className={`tool-message ${message.type}`}>{message.text}</div>}

      {loading ? (
        <div className="tool-loading">Loading earnings...</div>
      ) : (
        <>
          <section className="tool-card">
            <h2>Available for Cash Out</h2>
            <p className="summary-amount">{formatMoney(netCashOutAmount)}</p>
            <p className="tool-row-sub" style={{ marginTop: 8 }}>
              Gross earnings {formatMoney(availableEarnings)}
              {payoutChargesTotal > 0 ? ` · Deductions ${formatMoney(payoutChargesTotal)}` : ''}
            </p>
            <div className="tool-inline" style={{ marginTop: 12 }}>
              <button
                className="tool-btn"
                type="button"
                onClick={() => setCashOutOpen(true)}
                disabled={saving || netCashOutAmount <= 0 || !availableCharges.length}
              >
                Cash Out
              </button>
              <Link className="tool-btn-secondary" to="/account/payments/wallet">
                Manage payout methods
              </Link>
            </div>
          </section>

          <section className="tool-card">
            <h2>{activityTab === 'earnings' ? 'Earnings: Payments Received' : 'Earnings: Payouts'}</h2>
            <div className="tool-tabs" aria-label="Earnings activity">
              <button
                className={activityTab === 'earnings' ? 'active' : ''}
                type="button"
                onClick={() => setActivityTab('earnings')}
              >
                Earnings
              </button>
              <button
                className={activityTab === 'payouts' ? 'active' : ''}
                type="button"
                onClick={() => setActivityTab('payouts')}
              >
                Payouts
              </button>
            </div>
            <PaymentRows
              rows={activityRows}
              empty={
                activityTab === 'payouts'
                  ? "You haven't received any payouts yet."
                  : "You haven't received any payments for tasks yet."
              }
            />
          </section>
        </>
      )}

      {cashOutOpen && (
        <div className="tool-modal" role="dialog" aria-modal="true">
          <div className="tool-modal-card">
            <div className="tool-modal-head">
              <div>
                <h3>Cash Out Confirmation</h3>
                <p>Net amount: {formatMoney(netCashOutAmount)}</p>
              </div>
              <button className="tool-close" type="button" onClick={() => setCashOutOpen(false)}>
                x
              </button>
            </div>
            <div className="tool-form">
              <p>
                Default payout:{' '}
                {defaultPayoutAccount ? methodLabel(defaultPayoutAccount) : 'None — add one in Wallet'}
              </p>
              <div className="tool-inline">
                <button className="tool-btn-secondary" type="button" onClick={() => setCashOutOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button className="tool-btn" type="button" onClick={handleCashOut} disabled={saving}>
                  {saving ? 'Processing...' : 'Confirm Cash Out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Earnings;
